import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';

const BiscuitTrail = () => {
  const { currentUser } = useAuth();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Define the cards with their content
  const initialCards = [
    {
      id: 'card-1',
      title: 'PRAYING FOR PEOPLE',
      subtitle: 'How to start',
      color: 'bg-red-100'
    },
    {
      id: 'card-2',
      title: 'BUILDING A FRIENDSHIP',
      subtitle: 'How to start',
      color: 'bg-orange-100'
    },
    {
      id: 'card-3',
      title: 'LIVING A LIFE THAT DEMANDS EXPLANATION',
      subtitle: 'How to start',
      color: 'bg-yellow-100'
    },
    {
      id: 'card-4',
      title: 'TELLING SOMEONE YOU ARE A CHRISTIAN',
      subtitle: 'How to start',
      color: 'bg-green-100'
    },
    {
      id: 'card-5',
      title: 'EARNING SOMEONE\'S TRUST',
      subtitle: 'How to start',
      color: 'bg-teal-100'
    },
    {
      id: 'card-6',
      title: 'FINDING OUT SOMEONE\'S SPIRITUAL BACKGROUND',
      subtitle: 'How to start',
      color: 'bg-blue-100'
    },
    {
      id: 'card-7',
      title: 'FINDING OUT SOMEONE\'S PERCEPTION OF JESUS',
      subtitle: 'How to start',
      color: 'bg-indigo-100'
    },
    {
      id: 'card-8',
      title: 'SHOWING HOW JESUS IS RELEVANT TO LIFE',
      subtitle: 'How to start',
      color: 'bg-purple-100'
    },
    {
      id: 'card-9',
      title: 'SHARING YOUR STORY',
      subtitle: 'How to start',
      color: 'bg-pink-100'
    },
    {
      id: 'card-10',
      title: 'INVITING THEM TO EXPERIENCE CHRISTIAN COMMUNITY',
      subtitle: 'How to start',
      color: 'bg-rose-100'
    },
    {
      id: 'card-11',
      title: 'SHARING THE GOOD NEWS OF JESUS',
      subtitle: 'How to start',
      color: 'bg-amber-100'
    },
    {
      id: 'card-12',
      title: 'OPENING THE BIBLE WITH SOMEONE',
      subtitle: 'How to start',
      color: 'bg-lime-100'
    }
  ];

  const [cards, setCards] = useState(initialCards);

  // Add this function to split cards into rows
  const getCardRows = (cards) => {
    const rows = [];
    for (let i = 0; i < cards.length; i += 4) {
      rows.push(cards.slice(i, i + 4));
    }
    return rows;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(cards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCards(items);
  };

  const handleSubmitOrder = () => {
    setShowFeedback(true);
    setFeedback("Great job organizing your discipleship journey! This order reflects your understanding of the discipleship process. Consider how each step builds upon the previous ones.");
  };

  const handleReset = () => {
    setCards(initialCards);
    setShowFeedback(false);
    setFeedback('');
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          The Biscuit Trail Challenge
        </h1>
        <p className="text-gray-600 mb-4">
          Arrange these cards in the order you think would be most effective for helping someone discover Jesus.
        </p>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex flex-col gap-2 sm:gap-4">
          {getCardRows(cards).map((row, rowIndex) => (
            <StrictModeDroppable
              key={`row-${rowIndex}`}
              droppableId={`row-${rowIndex}`}
              direction="horizontal"
            >
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex gap-2 sm:gap-4"
                >
                  {row.map((card, index) => (
                    
                    <Draggable
                      key={card.id}
                      draggableId={card.id}
                      index={rowIndex * 4 + index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`
                            ${card.color} 
                            rounded-lg 
                            p-2 sm:p-4
                            ${snapshot.isDragging ? 'shadow-xl ring-2 ring-[#5ABA47] opacity-90 z-50' : 'shadow-sm hover:shadow-md'}
                            transition-shadow
                            cursor-move
                            flex-1
                            min-w-[75px] sm:min-w-[150px]
                          `}
                          style={{
                            ...provided.draggableProps.style,
                          }}
                        >
                          <div className="h-full flex flex-col">
                            <p className="text-xs text-gray-500 mb-1 hidden sm:block">{card.subtitle}</p>
                            <h3 className="font-semibold text-gray-900 flex-grow text-xs sm:text-base">
                              {card.title}
                            </h3>
                            <div className="mt-1 sm:mt-2 text-gray-400 text-xs sm:text-sm">
                              #{rowIndex * 4 + index + 1}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          ))}
        </div>
      </DragDropContext>

      <div className="mt-8 flex justify-center gap-4">
        <button
          onClick={handleSubmitOrder}
          className="px-6 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939] transition-colors"
        >
          Submit Order
        </button>
        <button
          onClick={handleReset}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Your Biscuit Trail Order</h3>
            <p className="text-gray-600 mb-6">{feedback}</p>
            <button
              onClick={() => setShowFeedback(false)}
              className="w-full px-4 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939]"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiscuitTrail; 